import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/UI/Button';
import Modal from 'components/Modal';
import { REQUIRED_ANGLE_PHOTOS } from 'constants/digitalLoanPhase2';
import { areRequiredPhotosComplete } from '../utils/phase2MockMapping';
import VerificationLoaderModal from './VerificationLoaderModal';

const TrashIcon = () => (
  <svg
    width='18'
    height='18'
    viewBox='0 0 24 24'
    aria-hidden='true'
    focusable='false'
  >
    <path
      fill='currentColor'
      d='M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z'
    />
  </svg>
);

const PhotoReviewScreen = ({
  literals,
  photos,
  onValidate,
  onClearPhotos,
  onEdit,
  onBack,
  isSubmitting = false,
  requireDocument = false,
}) => {
  const [confirmClear, setConfirmClear] = useState(false);
  const [previews, setPreviews] = useState({
    front: null,
    angles: [],
    label: null,
    document: null,
  });

  useEffect(() => {
    const next = {
      front: photos.front ? URL.createObjectURL(photos.front) : null,
      angles: (photos.angles || []).map((file) => (
        file ? URL.createObjectURL(file) : null
      )),
      label: photos.label ? URL.createObjectURL(photos.label) : null,
      document: photos.document ? URL.createObjectURL(photos.document) : null,
    };
    setPreviews(next);
    return () => {
      if (next.front) URL.revokeObjectURL(next.front);
      next.angles.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      if (next.label) URL.revokeObjectURL(next.label);
      if (next.document) URL.revokeObjectURL(next.document);
    };
  }, [photos]);

  const canValidate = areRequiredPhotosComplete(photos, requireDocument)
    && !isSubmitting;
  const hasAnyPhoto = Boolean(
    photos.front
    || photos.label
    || photos.document
    || (photos.angles || []).some(Boolean),
  );

  const slots = [
    { key: 'front', label: literals.frontLabel, url: previews.front },
    ...Array.from({ length: REQUIRED_ANGLE_PHOTOS }).map((_, index) => ({
      key: `angle-${index}`,
      label: (literals.angleSlotLabels || [])[index],
      url: previews.angles[index],
    })),
    { key: 'label', label: literals.labelLabel, url: previews.label },
    ...(requireDocument ? [{
      key: 'document',
      label: literals.documentLabel,
      url: previews.document,
    }] : []),
  ];

  return (
    <div className='full-screen-container digital-loan-photo-review'>
      <div className='screen-content'>
        <h1 className='digital-loan-phase2__title'>{literals.title}</h1>
        <p className='digital-loan-phase2__body mb-4'>{literals.subtitle}</p>
        {slots.map((slot) => (
          <div key={slot.key} className='digital-loan-photo-review__item'>
            <p className='digital-loan-photo-capture__caption'>{slot.label}</p>
            {slot.url ? (
              <img
                src={slot.url}
                alt={slot.label || ''}
                className='digital-loan-photo-review__thumb'
              />
            ) : (
              <p className='digital-loan-photo-capture__hint'>{literals.emptySlot}</p>
            )}
            <button
              type='button'
              className='digital-loan-photo-capture__change'
              onClick={onEdit}
              disabled={isSubmitting}
            >
              {literals.changePhoto}
            </button>
          </div>
        ))}
      </div>
      <div className='screen-actions'>
        <Button
          text={literals.validate}
          disabled={!canValidate}
          loading={isSubmitting}
          onClick={onValidate}
          limitToOneClick
        />
        <Button
          type='secondary'
          disabled={!hasAnyPhoto || isSubmitting}
          onClick={() => setConfirmClear(true)}
        >
          <TrashIcon />
          <span>{literals.deleteAll}</span>
        </Button>
        <Button
          text={literals.back}
          type='secondary'
          onClick={onBack}
          disabled={isSubmitting}
        />
      </div>
      {isSubmitting && (
        <VerificationLoaderModal literals={literals.validationLoader} />
      )}
      {confirmClear && (
        <Modal
          className='digital-loan-visit-modal'
          closeOnBackgroundClick={false}
          showCloseButton={false}
          onClose={() => setConfirmClear(false)}
        >
          <h2 className='digital-loan-visit-modal__title'>
            {literals.deleteConfirmTitle}
          </h2>
          <p className='digital-loan-phase2__body mb-4'>
            {literals.deleteConfirmBody}
          </p>
          <Button
            text={literals.confirmDelete}
            onClick={() => {
              setConfirmClear(false);
              onClearPhotos();
            }}
          />
          <Button
            text={literals.cancelDelete}
            type='secondary'
            onClick={() => setConfirmClear(false)}
          />
        </Modal>
      )}
    </div>
  );
};

PhotoReviewScreen.propTypes = {
  literals: PropTypes.object.isRequired,
  photos: PropTypes.object.isRequired,
  onValidate: PropTypes.func.isRequired,
  onClearPhotos: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  requireDocument: PropTypes.bool,
};

export default PhotoReviewScreen;
